import { action, computed, makeObservable, observable, reaction } from 'mobx';

import ApiService from '../services/ApiService';
import { parseParams } from '../util/ParamsUtil';

import { ILanguageStore } from './LanguageStore';

export interface ITranslationMapping {
  globalTranslations: Record<string, string>;
  billingTranslations: Record<string, string>;
}

export interface ITranslationParameterMapping {
  [value: string]: any;
}

export interface ITranslateStore {
  init?: () => void;

  isLoaded: boolean;

  translate(key: string, paramObject?: ITranslationParameterMapping): string;

  translateHtml(key: string, paramObject?: ITranslationParameterMapping): Html;

  globalTranslations: Record<string, string>;

  billingTranslations: Record<string, string>;
}

export interface Html {
  __html: string;
}

class TranslateStore implements ITranslateStore {
  @observable
  public isLoaded = false;
  @observable
  private translations: ITranslationMapping = { globalTranslations: {}, billingTranslations: {} };

  private loaderPlaceholder = '...';

  constructor(
    private languageStore: ILanguageStore,
    private apiService: ApiService
  ) {
    makeObservable(this);
    this.translate = this.translate.bind(this);
  }

  @computed
  public get globalTranslations(): Record<string, string> {
    return this.translations.globalTranslations;
  }

  @computed
  public get billingTranslations(): Record<string, string> {
    return this.translations.billingTranslations;
  }

  @action
  public init(): void {
    reaction(
      () => this.languageStore.currentLanguage,
      (lang) => this.updateTranslations(lang)
    );
    this.updateTranslations(this.languageStore.currentLanguage);
  }

  @action
  private markLoaded() {
    this.isLoaded = true;
  }

  @action
  private setTranslations = (translations: ITranslationMapping) => {
    this.translations = translations || { globalTranslations: {}, billingTranslations: {} };
  };

  @action.bound
  private updateTranslations(lang: string) {
    return this.apiService
      .getTranslations(lang)
      .then((translations) => {
        this.setTranslations(translations);
        this.markLoaded();
      })
      .catch(() => {
        this.markLoaded();
      });
  }

  public translate(key: string, paramObject?: ITranslationParameterMapping): string {
    return this.getTranslationForGroup(key, paramObject);
  }

  public translateHtml = (key: string, paramObject?: ITranslationParameterMapping): Html => {
    return { __html: this.translate(key, paramObject) };
  };

  private getTranslationForGroup = (key: string, paramObject?: ITranslationParameterMapping) => {
    const textValue = this.translations.billingTranslations[key] || this.translations.globalTranslations[key];

    if (textValue && paramObject) {
      return parseParams(textValue, paramObject);
    }

    if (textValue) {
      return textValue;
    } else if (this.isLoaded) {
      return key;
    } else {
      return this.loaderPlaceholder;
    }
  };
}

export default TranslateStore;
